import { Link, Button, IconButton, useMediaQuery, useTheme, Checkbox } from '@mui/material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { DeleteDialog } from '../../FavoritesDialogs';

import {
  ActivityKindEnum,
  RentPeriodEnum,
  PropertyKindEnum,
  TransactionTypeEnum,
  HeaderDialogsEnum,
  UserModel,
  AdCollectionModel,
} from '@/types';
import { priceFormat } from '@/utils';
import styles from './AdsCollections.module.scss';
import { PDFIcon, MarkerIcon, DeleteIcon, CalendarIcon } from '../../svg';
import { shouldRestrictByTariff } from '@/utils/tariffPolicy';

interface AdsListItemProps {
  item: AdCollectionModel;
  handleCheckboxChange: (id: string) => void;
  selectedAds: string[];
  user: UserModel;
  handleClickOpenDialog?: (value: HeaderDialogsEnum) => void;
  deleteListing: () => void;
  onClose: () => void;
  open: boolean;
  onOpenDelete: (value: string) => void;
}

type PreferredPropertyKinds =
  | 'shop'
  | 'office'
  | 'warehouse'
  | 'residentialLand'
  | 'industrialLand'
  | 'commercialLand';

export const AdsCollections = ({
  item: {
    activityKind,
    nameEn,
    addressEn,
    id,
    propertyAdMedia,
    updatedAt,
    convertedPrice,
    convertedCurrency,
    convertedPriceYearly,
    convertedPriceMonthly,
    convertedPriceWeekly,
    convertedPriceDaily,
    defaultRentFrequency,
    propertyKind,
    transactionType,
    convertedSellingPrice,
    locationId,
    fromXml,
  },
  handleCheckboxChange,
  selectedAds,
  user,
  handleClickOpenDialog,
  deleteListing,
  onClose,
  open,
  onOpenDelete,
}: AdsListItemProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isTariffFree = shouldRestrictByTariff(user);

  const activityKindLabels = {
    [ActivityKindEnum.Sale]: 'Sale',
    [ActivityKindEnum.Rent]: 'Rent',
  };
  const rentFrequencyLabels = {
    [RentPeriodEnum.Yearly]: 'Yearly',
    [RentPeriodEnum.Monthly]: 'Monthly',
    [RentPeriodEnum.Weekly]: 'Weekly',
    [RentPeriodEnum.Daily]: 'Daily',
  };

  const preferredPropertyKinds = [
    PropertyKindEnum.Shop,
    PropertyKindEnum.Office,
    PropertyKindEnum.Warehouse,
    PropertyKindEnum.ResidentialLand,
    PropertyKindEnum.IndustrialLand,
    PropertyKindEnum.CommercialLand,
  ];

  const propertyKindLabel: Record<PreferredPropertyKinds, string> = {
    shop: 'Shop',
    office: 'Office',
    warehouse: 'Warehouse',
    residentialLand: 'Residential Land',
    industrialLand: 'Industrial Land',
    commercialLand: 'Commercial Land',
  };

  const removeAfterLastComma = (str: string) => {
    const lastCommaIndex = str.lastIndexOf(',');
    if (lastCommaIndex === -1) {
      return str;
    }
    return str.substring(0, lastCommaIndex);
  };

  const isPrefTitle = preferredPropertyKinds.includes(propertyKind);
  const titlePref = propertyKind && propertyKindLabel[propertyKind as PreferredPropertyKinds];
  const titleText = isPrefTitle ? `${titlePref} at ${nameEn}` : nameEn;
  const addressText = locationId ? removeAfterLastComma(addressEn || nameEn) : addressEn || nameEn;
  const activityLabel = activityKindLabels[activityKind];

  const isSale = activityKind !== ActivityKindEnum.Rent;

  let newPrice = 0;
  if (isSale) {
    if (transactionType === TransactionTypeEnum.Distress) {
      newPrice = convertedSellingPrice || 0;
    } else {
      newPrice = convertedPrice || 0;
    }
  } else {
    switch (defaultRentFrequency) {
      case RentPeriodEnum.Yearly:
        newPrice = convertedPriceYearly || 0;
        break;
      case RentPeriodEnum.Monthly:
        newPrice = convertedPriceMonthly || 0;
        break;
      case RentPeriodEnum.Weekly:
        newPrice = convertedPriceWeekly || 0;
        break;
      case RentPeriodEnum.Daily:
        newPrice = convertedPriceDaily || 0;
        break;
    }
  }

  const isVerifyRequestLocation = !locationId && !fromXml;

  const imgUrl = isMobile
    ? `url('${propertyAdMedia[0]?.fileUrl}_382x133')`
    : `url('${propertyAdMedia[0]?.fileUrl}_88x88')`;

  return (
    <div className={styles.listItem}>
      <Checkbox
        className={styles.itemCheckbox}
        icon={<span />}
        checked={selectedAds.includes(id)}
        checkedIcon={<span className={styles.checked} />}
        onChange={() => handleCheckboxChange(id)}
      />
      <div
        className={styles.listItemImage}
        style={{
          backgroundImage: propertyAdMedia.length > 0 ? `${imgUrl}` : `url('/Img/newLogo.png')`,
        }}
      >
        {isVerifyRequestLocation && (
          <div className={styles.notLocation}>location not confirmed</div>
        )}
      </div>
      <div className={styles.addressBlock}>
        <div className={styles.titleBlock}>
          <Link href={`/ads/${id}`} title={titleText} className={styles.title}>
            {titleText}
          </Link>
          <div className={styles.adsId}>#{id}</div>
        </div>
        {(addressEn || nameEn) && (
          <div
            className={`${styles.itemAddress} ${isVerifyRequestLocation ? styles.itemAddressRed : styles.itemAddressBlack}`}
          >
            <div className={styles.menuIcon}>
              <MarkerIcon />
            </div>
            <span className={styles.addressText}>{addressText}</span>
          </div>
        )}
      </div>
      <div className={styles.priceDateBlock}>
        {newPrice ? (
          <div className={styles.currency}>
            {activityLabel}: <span>{priceFormat(newPrice)}</span>
            {convertedCurrency}
            {!isSale && (
              <div className={styles.frequency}>
                <span style={{ fontSize: '14px', color: '#1650FF', fontWeight: 400 }}>
                  {defaultRentFrequency && rentFrequencyLabels[defaultRentFrequency]}
                </span>
              </div>
            )}
          </div>
        ) : null}
        <div className={styles.date}>
          <div className={styles.menuIcon}>
            <CalendarIcon />
          </div>
          Update: <span>{moment(updatedAt).format('D MMMM, h:mm a')}</span>
        </div>
      </div>
      <div className={styles.pdfButtonSection}>
        <Button
          variant='contained'
          startIcon={<PDFIcon />}
          onClick={() =>
            isTariffFree
              ? handleClickOpenDialog?.(HeaderDialogsEnum.Tariff)
              : navigate(`/ads/create-pdf/${id}`)
          }
          sx={{
            color: '#2A3842',
            backgroundColor: '#C5DFFF',
            textTransform: 'none',
            borderRadius: '14px',
            flex: 1,
            ':hover': {
              color: '#FFF',
            },
          }}
        >
          Save&nbsp;as&nbsp;PDF
        </Button>
        <IconButton
          sx={{
            width: '40px',
            height: '40px',
            ':hover': {
              color: '#1650FF',
            },
          }}
          onClick={() => onOpenDelete(id)}
        >
          <DeleteIcon />
        </IconButton>
      </div>
      <DeleteDialog
        title='Are you sure you want to delete this listing from collection?'
        open={open}
        onClose={onClose}
        onClick={deleteListing}
      />
    </div>
  );
};
